import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Building2, Check, ChevronDown } from "lucide-react";

/** Top-right client/tenant switcher — pattern from Incentra.
 *  Hidden when the user has access to just one client. */
export default function ClientSwitcher() {
  const { clients, active, activeSlug, switchClient, loading } = useClient();

  if (loading || !active) return null;
  if (!clients || clients.length <= 1) {
    return (
      <div
        data-testid="client-badge"
        className="hidden sm:flex items-center gap-2 pl-2 pr-3 h-8 rounded-md bg-slate-50 border border-slate-200"
      >
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: active.brand_color || "#4F46E5" }}
        />
        <span className="text-xs font-medium text-slate-700 truncate max-w-[160px]">
          {active.name}
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="client-switcher"
          className="h-8 gap-2 border-slate-200 bg-white text-slate-700 pl-2 pr-2.5"
        >
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: active.brand_color || "#4F46E5" }}
          />
          <span className="text-xs font-medium truncate max-w-[160px]">
            {active.name}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="client-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <Building2 className="w-3 h-3" />
          Switch Client
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {clients.map((c) => (
          <DropdownMenuItem
            key={c.slug}
            onClick={() => switchClient(c.slug)}
            data-testid={`client-option-${c.slug}`}
            className="flex items-center gap-2 cursor-pointer"
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: c.brand_color || "#4F46E5" }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
              {c.description && (
                <div className="text-[11px] text-slate-500 truncate">{c.description}</div>
              )}
            </div>
            {activeSlug === c.slug && <Check className="w-3.5 h-3.5 text-indigo-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
